import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import AdminSidebar from './AdminSidebar';
import Register from './Register';
import { API_BASE } from './config';
import '../css/AdminCheckinPage.css';

function AdminCheckinPage() {
  const navigate = useNavigate();
  const params = useParams();
  const groupId = Number(params.groupId || localStorage.getItem('last_group_id') || 0);
  const actor = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const [group, setGroup] = useState(null);
  const [qrImage, setQrImage] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (String(actor?.role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
    if (!groupId) {
      setError('กรุณาเลือกก๊วนก่อนเปิดหน้า Check-in');
      setLoading(false);
      return;
    }
    localStorage.setItem('last_group_id', String(groupId));
    fetch(`${API_BASE}/api/groups`)
      .then(response => response.ok ? response.json() : Promise.reject(new Error('โหลดข้อมูลก๊วนไม่สำเร็จ')))
      .then(data => {
        const groups = Array.isArray(data) ? data : (data.groups || []);
        setGroup(groups.find(item => Number(item.id) === groupId) || null);
      })
      .catch(loadError => setError(loadError.message));
  }, [actor?.role, groupId, navigate]);

  const createQr = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/checkin/join/${groupId}/qr-code`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'สร้าง Check-in QR ไม่สำเร็จ');
      const url = `${window.location.origin}/join/${groupId}?code=${encodeURIComponent(data.code)}`;
      const image = await QRCode.toDataURL(url, {
        width: 520,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: { dark: '#102a43', light: '#ffffff' }
      });
      setTargetUrl(url);
      setQrImage(image);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { createQr(); }, [createQr]);

  const copyLink = async () => {
    await navigator.clipboard.writeText(targetUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <main className="admin-checkin-page admin-shell-with-sidebar">
      <AdminSidebar active="checkin" groupId={groupId} groupName={group?.Group_Name} />
      <header>
        <div><small>GROUP CHECK-IN</small><h1>▦ Check-in สมาชิกและ QR</h1><p>{group?.Group_Name || `Group #${groupId}`}</p></div>
        <button type="button" onClick={() => navigate('/dashboard')}>← Dashboard</button>
      </header>

      {error && <div className="admin-checkin-error">⚠️ {error}</div>}
      <div className="admin-checkin-grid">
        <section className="admin-checkin-register">
          <div className="admin-checkin-section-title"><b>▦ Check-in สมาชิก</b><span>ค้นหาสมาชิกเดิม หรือเพิ่มสมาชิกใหม่เพื่อ Check-in เข้าก๊วนนี้</span></div>
          <Register embedded initialGroupId={groupId} />
        </section>

        <section className="admin-checkin-qr">
          <div className="admin-checkin-section-title"><b>▦ QR CHECK-IN</b><span>ให้ผู้เล่นสแกนเพื่อ Check-in เข้าก๊วน</span></div>
          {loading ? <p>กำลังสร้าง QR…</p> : qrImage ? <>
            <img src={qrImage} alt={`Check-in QR ${group?.Group_Name || groupId}`} />
            <code>{targetUrl}</code>
            <div className="admin-checkin-qr-actions">
              <button type="button" onClick={copyLink}>{copied ? '✓ คัดลอกแล้ว' : '🔗 คัดลอกลิงก์'}</button>
              <a href={qrImage} download={`checkin-${group?.Group_Name || groupId}.png`}>⬇ ดาวน์โหลด QR</a>
              <a href={targetUrl} target="_blank" rel="noreferrer">↗ เปิดหน้าปลายทาง</a>
            </div>
          </> : <p>ไม่สามารถสร้าง QR ได้</p>}
        </section>
      </div>
    </main>
  );
}

export default AdminCheckinPage;
